import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import PublicDPTPage from "@/pages/PublicDPTPage";
import AppLayout from "@/components/AppLayout";
import DashboardPage from "@/pages/DashboardPage";
import VotersPage from "@/pages/VotersPage";
import UsersPage from "@/pages/UsersPage";
import WilayahPage from "@/pages/WilayahPage";
import CoklitPage from "@/pages/CoklitPage";
import AuditPage from "@/pages/AuditPage";
import SettingsPage from "@/pages/SettingsPage";
import ImportPage from "@/pages/ImportPage";
import ReportsPage from "@/pages/ReportsPage";
import DpsDeltaPage from "@/pages/DpsDeltaPage";
import DeltaAndLogPage from "@/pages/DeltaAndLogPage";
import LockProgressPage from "@/pages/LockProgressPage";
import RwLoginPage from "@/pages/RwLoginPage";
import RwDashboardPage from "@/pages/RwDashboardPage";
import RwOfficersPage from "@/pages/RwOfficersPage";
import NewsPage from "@/pages/NewsPage";
import PublicArticlePage from "@/pages/PublicArticlePage";
import PublicNewsListPage from "@/pages/PublicNewsListPage";
import ResetVotersPage from "@/pages/ResetVotersPage";
import PpdpMappingSimulationPage from "@/pages/PpdpMappingSimulationPage";
import RecycleBinPage from "@/pages/RecycleBinPage";
import MyChangesPage from "@/pages/MyChangesPage";
import PpdpChangesReviewPage from "@/pages/PpdpChangesReviewPage";
import InstallPrompt from "@/components/InstallPrompt";

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">
        <div className="animate-pulse text-sm tracking-widest uppercase">Memuat...</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return null;
  // All authenticated users can see the dashboard
  return <Navigate to="/app/dashboard" replace />;
}

function App() {
  return (
    <AuthProvider>
      <OfflineProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicDPTPage />} />
          <Route path="/berita" element={<PublicNewsListPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/rw" element={<RwLoginPage />} />
          <Route path="/rw/dashboard" element={<RwDashboardPage />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<RoleHome />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="voters" element={<VotersPage />} />
            <Route path="coklit" element={<CoklitPage />} />
            <Route
              path="verifikasi"
              element={<Navigate to="/app/lock-progress" replace />}
            />
            <Route
              path="users"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="wilayah"
              element={
                <ProtectedRoute roles={["admin", "panitia"]}>
                  <WilayahPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="import"
              element={
                <ProtectedRoute roles={["admin", "panitia"]}>
                  <ImportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute roles={["admin", "panitia"]}>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="dps-delta"
              element={
                <ProtectedRoute roles={["admin", "panitia", "rt", "rw"]}>
                  <DeltaAndLogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="lock-progress"
              element={
                <ProtectedRoute roles={["admin", "panitia"]}>
                  <LockProgressPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="audit"
              element={
                <ProtectedRoute roles={["admin", "panitia"]}>
                  <AuditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rw-officers"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <RwOfficersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="news"
              element={
                <ProtectedRoute roles={["admin", "panitia"]}>
                  <NewsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="reset-voters"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <ResetVotersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="ppdp-mapping-simulasi"
              element={
                <ProtectedRoute roles={["admin", "panitia"]}>
                  <PpdpMappingSimulationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="recycle-bin"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <RecycleBinPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="perubahan-saya"
              element={
                <ProtectedRoute roles={["ppdp", "admin", "panitia"]}>
                  <MyChangesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="log-perubahan-ppdp"
              element={<Navigate to="/app/dps-delta" replace />}
            />
          </Route>
          <Route path="/:slug" element={<PublicArticlePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-center" />
      <InstallPrompt />
      </OfflineProvider>
    </AuthProvider>
  );
}

export default App;
